import { useDashboardContext } from '@/contexts/DashboardContext';
import { classNames } from '@/utils';
import { PlanGroup, PlanType } from '@superfunnel/types';
import { createPlanComparisonMap } from '@superfunnel/utils';
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import pricingBackground from '../../assets/images/pricingBackground.png';
import customPlanBackground from '../../assets/images/pricingCustomBackground.png';
import { SnackbarContext } from '../../contexts/SnackbarContext';
import { UserContext } from '../../contexts/UserContext';
import {
  createCheckoutSession,
  getAvailablePlans,
} from '../../services/BillingService';
import PlanCard from './components/PlanCard';

type BillingPeriod = 'monthly' | 'annual';

// SVG Components
const CheckmarkIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="relative h-4 w-4 shrink-0 grow-0"
    preserveAspectRatio="xMidYMid meet"
  >
    <path
      d="M3.33325 8.66699L5.99992 11.3337L12.6666 4.66699"
      stroke="#795DFC"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface BillingProps {
  whiteMode?: boolean;
  header?: ReactNode;
  customPlanLayout?: 'bottom' | 'inline';
}

export default function SubscriptionPlans({
  whiteMode = false,
  header,
  customPlanLayout = 'bottom',
}: BillingProps) {
  const { selectedAccount } = useContext(UserContext);
  const { show: showSnackbar } = useContext(SnackbarContext);
  const { subscription } = useDashboardContext();
  const [planGroups, setPlanGroups] = useState<PlanGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState<PlanType | null>(null);
  const [billingPeriod, _setBillingPeriod] = useState<BillingPeriod>('monthly');
  const [selectedTiers, setSelectedTiers] = useState<Record<string, PlanType>>(
    {},
  );

  const accountId = selectedAccount?.id;

  // Only consider the plan as "current" if the subscription is not expired
  // For expired subscriptions, treat as no plan so user can re-subscribe
  // Note: "cancelled" subscriptions are still active until endsAt, so keep showing current plan
  const currentPlan =
    selectedAccount?.data().billing?.subscriptionStatus === 'expired'
      ? undefined
      : selectedAccount?.data().billing?.currentPlan;

  // Create plan comparison map based on current plan
  const changePlansMap = useMemo(
    () => createPlanComparisonMap(planGroups, currentPlan),
    [planGroups, currentPlan],
  );

  const handleManagePlan = async () => {
    if (!accountId) {
      showSnackbar('No account selected', 'warn');
      return;
    }

    try {
      const subscriptionData = subscription?.data();
      if (subscriptionData?.urls?.customerPortal) {
        const redirectUrl = encodeURIComponent(window.location.href);
        const url = new URL(subscriptionData.urls.customerPortal);
        url.searchParams.append('redirect_url', redirectUrl);
        window.location.href = url.toString();
      } else {
        showSnackbar('Unable to access plan management', 'warn');
      }
    } catch (error) {
      console.error('Failed to get subscription details:', error);
      showSnackbar('Failed to open plan management', 'warn');
    }
  };

  const loadPlans = useCallback(async () => {
    try {
      const response = await getAvailablePlans();
      setPlanGroups(response);
    } catch (error) {
      console.error('Failed to load plans:', error);
      showSnackbar('Failed to load plans', 'warn');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const handleSelectPlan = async (planId: PlanType) => {
    if (!accountId) {
      showSnackbar('No account selected', 'warn');
      return;
    }

    if (planId === 'custom') {
      window.open(
        'https://calendly.com/superfunnelai/sales',
        '_blank',
        'noopener,noreferrer',
      );
      return;
    }

    // Find the selected tier to get its variant ID
    let variantId: string | undefined;
    for (const group of planGroups) {
      const tier = group.tiers.find((t) => t.id === planId);
      if (tier) {
        // For now, always use monthly variant ID
        // TODO: Support yearly billing when available
        variantId = tier.variantId;
        break;
      }
    }

    if (!variantId) {
      showSnackbar('Invalid plan selected', 'warn');
      return;
    }

    setCheckoutLoading(planId);
    try {
      const planRelationship = changePlansMap[planId];

      // If user has a subscription (upgrade/downgrade), use Lemon Squeezy portal
      // Otherwise, create a new checkout session
      if (planRelationship === 'upgrade' || planRelationship === 'downgrade') {
        const subscriptionData = subscription?.data();
        const updateUrl =
          subscriptionData?.urls?.customerPortalUpdateSubscription ||
          subscriptionData?.urls?.customerPortal;

        if (!updateUrl) {
          showSnackbar('Unable to access plan management', 'warn');
          setCheckoutLoading(null);
          return;
        }

        // Open Lemon Squeezy portal with pre-selected variant and redirect URL
        const redirectUrl = encodeURIComponent(window.location.href);
        const url = new URL(updateUrl);
        url.searchParams.append('variant', variantId);
        url.searchParams.append('redirect_url', redirectUrl);
        window.location.href = url.toString();
        setCheckoutLoading(null);
      } else {
        const response = await createCheckoutSession(
          accountId,
          variantId,
          window.location.href,
        );
        window.location.href = response.checkoutUrl;
      }
    } catch (error) {
      console.error('Failed to process plan selection:', error);
      showSnackbar('Failed to process request', 'warn');
      setCheckoutLoading(null);
    }
  };

  const handleTierChange = (groupId: string, tierId: PlanType) => {
    setSelectedTiers((prev) => ({
      ...prev,
      [groupId]: tierId,
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-[#795dfc]" />
      </div>
    );
  }

  // Separate regular plans from custom plan
  const regularPlans = planGroups.filter((group) => group.id !== 'custom');
  const customPlan = planGroups.find((group) => group.id === 'custom');

  return (
    <div className="relative flex flex-col items-center justify-start gap-7 px-4 pt-2 pb-5">
      {!whiteMode && (
        <img
          src={pricingBackground}
          alt="Pricing Background"
          className="absolute inset-0 -z-10 h-full w-full rounded-lg object-cover"
        />
      )}

      {/* Billing Period Toggle */}
      {/* <div className="mx-2 flex items-center justify-center gap-3 self-end rounded-full bg-white p-1 shadow-sm">
        <button
          onClick={() => setBillingPeriod('monthly')}
          className={`rounded-full px-6 py-2 text-sm font-medium transition-all ${
            billingPeriod === 'monthly'
              ? 'bg-[#795dfc] text-white'
              : 'text-[#414e62] hover:text-[#141c25]'
          }`}
        >
          Monthly
        </button>
        <button
          disabled
          onClick={() => setBillingPeriod('annual')}
          className={`rounded-full px-6 py-2 text-sm font-medium transition-all ${
            billingPeriod === 'annual'
              ? 'bg-[#795dfc] text-white'
              : 'text-[#414e62] hover:text-[#141c25]'
          }`}
        >
          Annual
        </button>
      </div> */}

      {/* Pricing Cards */}
      <div
        className={classNames(
          'mx-auto grid grid-cols-1 gap-4 self-stretch lg:grid-cols-2',
          !customPlan || customPlanLayout === 'bottom'
            ? 'lg:grid-cols-3'
            : 'xl:grid-cols-4',
        )}
      >
        {header && <div className="col-span-4">{header}</div>}
        {regularPlans.map((group) => {
          // Use current plan if it's in this group, otherwise use first tier
          const selectedTier =
            selectedTiers[group.id] ||
            (currentPlan && group.tiers.some((t) => t.id === currentPlan)
              ? currentPlan
              : group.tiers[0].id);

          return (
            <PlanCard
              key={group.id}
              groupId={group.id}
              groupName={group.name}
              description={group.description}
              tiers={group.tiers}
              selectedTierId={selectedTier}
              features={group.features}
              billingPeriod={billingPeriod}
              isFeatured={group.isFeatured}
              isLoading={checkoutLoading === selectedTier}
              changePlansMap={changePlansMap}
              onWhite={whiteMode}
              onSelectPlan={handleSelectPlan}
              onTierChange={handleTierChange}
              onManagePlan={handleManagePlan}
            />
          );
        })}
        {/* Custom Plan - Inline Layout */}
        {customPlan && customPlanLayout === 'inline' && (
          <PlanCard
            key={customPlan.id}
            groupId={customPlan.id}
            groupName={'Growth'}
            description={customPlan.description}
            tiers={customPlan.tiers}
            selectedTierId={'custom' as PlanType}
            features={customPlan.features}
            billingPeriod={billingPeriod}
            isFeatured={customPlan.isFeatured}
            isLoading={false}
            changePlansMap={changePlansMap}
            onWhite={whiteMode}
            onSelectPlan={handleSelectPlan}
            onTierChange={handleTierChange}
            onManagePlan={handleManagePlan}
          />
        )}
        {/* Custom Plan - Bottom Layout */}
        {customPlan && customPlanLayout === 'bottom' && (
          <div className="col-span-3 flex flex-col items-start justify-start gap-2.5 self-stretch">
            <div className="relative flex min-h-[187px] items-center justify-start self-stretch overflow-hidden rounded-3xl border border-[#e7deff] bg-white p-8">
              <div className="absolute inset-0">
                <img
                  src={customPlanBackground}
                  alt={`${customPlan.name} Background`}
                  className="h-full w-full"
                />
              </div>

              <div className="z-10 flex grow flex-col items-start justify-start gap-4.5">
                <div className="flex flex-col items-start justify-start gap-2 self-stretch">
                  <div className="flex items-center justify-start gap-4 self-stretch">
                    <p className="text-2xl font-medium text-[#141c25]">
                      {customPlan.name}
                    </p>
                  </div>
                  <p className="self-stretch text-base text-[#141c25]">
                    {customPlan.description}
                  </p>
                </div>

                <div className="flex items-start justify-start gap-2.5 self-stretch">
                  <div className="flex grow flex-col items-start justify-start gap-2">
                    {customPlan.features.slice(0, 2).map((feature) => (
                      <div
                        key={feature}
                        className="flex items-center justify-start gap-2"
                      >
                        <CheckmarkIcon />
                        <p className="text-sm text-[#141c25]">{feature}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex grow flex-col items-start justify-start gap-2">
                    {customPlan.features.slice(2, 4).map((feature) => (
                      <div
                        key={feature}
                        className="flex items-center justify-start gap-2"
                      >
                        <CheckmarkIcon />
                        <p className="text-sm text-[#141c25]">{feature}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <button
                onClick={() => handleSelectPlan('custom' as PlanType)}
                className="z-10 flex h-12 w-[340px] items-center justify-center gap-2.5 rounded-xl bg-[#795dfc] px-5 py-3 hover:bg-[#6b4eeb]"
              >
                <p className="text-base font-medium text-white">
                  Contact Sales
                </p>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
